import { SelectCategoryComboBox } from '@components/app/panels/right-panel/combo-box'
import {
  TrayPanelLayout,
  useAnyDropdownOpen,
  useControllableValue,
} from '@components/app/panels/right-panel/tray'
import { useMemo, useRef, useState } from 'react'
import { useTrayLazyWindow } from '@/hooks/use-tray-lazy-window'
import {
  CLIKART_ALL_CATEGORIES_OPTION,
  CLIKART_CATEGORIES,
} from '@/lib/clikart/clikart-category-filter'
import type { ClikArtPlacedFilter } from '@/lib/clikart/clikart-placed-filter'
import { computeClikArtActiveFilterCount } from '@/lib/clikart/clikart-active-filter-count'
import {
  CLIKART_SORT_DEFAULT,
  CLIKART_SORT_OPTIONS,
  type ClikArtSortValue,
} from '@/lib/clikart/clikart-sort'
import { ClikArtFilterControls } from './components/clikart-filter-controls'
import { ClikArtGallery } from './components/clikart-gallery'
import { useClikArtResults } from './hooks/use-clikart-results'

interface ClikartPanelProps {
  /** Reports whether the Category, Keyword, or Sort dropdown is currently open — lets a host
   *  panel (e.g. the Escape-key priority chain in
   *  `right-panel/root-panel/hooks/use-escape-closes-panel.ts`) know a dropdown-close should
   *  be left to Radix's own default Escape handling rather than also closing the whole panel. */
  onAnyDropdownOpenChange?: (open: boolean) => void
  /** Controlled category selection and filename search query — let a host
   *  (`right-panel/root-panel/hooks/use-clikart-filters.ts`) persist both across this panel's
   *  own unmount when the right panel collapses or switches to a different rail icon
   *  (WMPCM-10942 AC-9/AC-10). Each falls back to internal state when
   *  omitted, so the component still works standalone (e.g. in tests). */
  category?: string
  onCategoryChange?: (category: string) => void
  searchQuery?: string
  onSearchQueryChange?: (query: string) => void
  /** Controlled keyword selection (WMPCM-10954) — lifted like `category` so it survives this
   *  panel's own unmount. Falls back to internal state when omitted. */
  keywords?: string[]
  onKeywordsChange?: (keywords: string[]) => void
  /** Controlled Placed filter and Sort — lifted for the same reason as `category`/`keywords`, so
   *  the whole Filters section survives this panel's unmount. Each falls back to internal state
   *  when omitted. */
  placed?: ClikArtPlacedFilter
  onPlacedChange?: (placed: ClikArtPlacedFilter) => void
  sort?: ClikArtSortValue
  onSortChange?: (sort: ClikArtSortValue) => void
}

export function ClikArtPanel({
  onAnyDropdownOpenChange,
  category: categoryProp,
  onCategoryChange,
  searchQuery: searchQueryProp,
  onSearchQueryChange,
  keywords: keywordsProp,
  onKeywordsChange,
  placed: placedProp,
  onPlacedChange,
  sort: sortProp,
  onSortChange,
}: ClikartPanelProps) {
  const [category, setCategory] = useControllableValue(
    categoryProp,
    onCategoryChange,
    CLIKART_ALL_CATEGORIES_OPTION
  )
  const [searchQuery, setSearchQuery] = useControllableValue(
    searchQueryProp,
    onSearchQueryChange,
    ''
  )
  const [selectedKeywords, setSelectedKeywords] = useControllableValue<string[]>(
    keywordsProp,
    onKeywordsChange,
    []
  )
  const [placedFilter, setPlacedFilter] = useControllableValue<ClikArtPlacedFilter>(
    placedProp,
    onPlacedChange,
    'any'
  )
  const [sortValue, setSortValue] = useControllableValue<ClikArtSortValue>(
    sortProp,
    onSortChange,
    CLIKART_SORT_DEFAULT
  )

  // WMPCM-10948 AC-1: the Filters section (keyword search, keyword badges, Placed toggle,
  // filename search) starts collapsed.
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  // Figma node 277:23024 — "Show filename" switch in the panel body below the Filters section.
  // Off by default; display-only (does not feed the filter/sort pipeline below).
  const [showFilename, setShowFilename] = useState(false)

  const { keywordNames, keywordIds, isLoading, isError, results } = useClikArtResults({
    category,
    searchQuery,
    selectedKeywords,
    placedFilter,
    sortValue,
  })

  // WMPCM-10996 (US-11) — the real ClikArt library is ~12,900 items; rendering every matching
  // result at once would mean thousands of DOM nodes for an unfiltered/lightly-filtered search.
  // `useTrayLazyWindow` is the same windowing Photos already uses for its (also potentially large)
  // tray: render one chunk, grow by one chunk each time a sentinel below the grid scrolls into
  // view. `resetKey` collapses back to the first chunk on any control change, same as Photos —
  // otherwise a new filter could leave the window sized for the old, larger/smaller result set.
  const scrollRootRef = useRef<HTMLDivElement>(null)
  const {
    visibleItems: visibleResults,
    hasMore,
    sentinelRef,
  } = useTrayLazyWindow({
    items: results,
    scrollRootRef,
    resetKey: JSON.stringify([category, placedFilter, searchQuery, keywordIds, sortValue]),
  })

  // WMPCM-10948 AC-4
  const activeFilterCount = computeClikArtActiveFilterCount({
    selectedKeywordCount: selectedKeywords.length,
    placedIsRestricted: placedFilter !== 'any',
    filenameSearchQuery: searchQuery,
  })

  // Design update (2026-09-22, Figma re-annotated): the count chip and "Clear filters" both hide
  // when no filter is active and appear together in the Filters row (collapsed or expanded) once
  // one is — mirrors the Photos tray's existing `Clear filters` control, except that one lives
  // only inside its expanded body; this one lives in the row itself per the new annotation.
  // Deliberately scoped to what's inside the Filters section (keywords, Placed, filename search)
  // — Category is a separate control outside this section (WMPCM-10948's own AC boundary) and
  // the Photos tray's equivalent `handleClearFilters`
  // (`photos-panel/hooks/use-photos-tray-filters.ts`) likewise never touches its album selector.
  function handleClearFilters() {
    setSelectedKeywords([])
    setPlacedFilter('any')
    setSearchQuery('')
  }

  // Must stay after `useTrayLazyWindow`: its unmount cleanup effect runs after the results and
  // lazy-window effects, as it always has.
  const reportAnyDropdownOpenChange = useAnyDropdownOpen(
    { category: false, keyword: false, sort: false, preview: false },
    onAnyDropdownOpenChange
  )
  // Hover/focus thumbnail previews are Radix popovers too (same as Photos): report them so an
  // open preview takes the Escape press instead of the whole panel closing.
  const reportPreviewOpenChange = useMemo(
    () => reportAnyDropdownOpenChange('preview'),
    [reportAnyDropdownOpenChange]
  )

  // WMPCM-11930: the whole panel (Category, Filters, Show filename, results/Sort and the gallery)
  // scrolls as one box at the parent container, like Photos and Backgrounds —
  // `TrayPanelLayout` is that shared scroll container. It also keeps the WMPCM-11301 fix: its
  // `[scrollbar-gutter:stable]` reserves the scrollbar's width up front, so the Filters row can
  // never be shifted or clipped when the gallery grows tall enough to need scrolling. It is also
  // the `useTrayLazyWindow` scroll root.
  //
  // WMPCM-10984 (US-9) — live result count and WMPCM-10990 (US-10) — Sort come from the layout's
  // shared `TrayResultsToolbar` / `TraySortButton` (same Newest/Oldest/A-Z/Z-A options
  // `sortClikArt` sorts by).
  return (
    <>
      <div className="right-panel h-px border-b border-border m-0 p-0 -ms-9 -me-2 mb-4" />
      <TrayPanelLayout
        scrollRootRef={scrollRootRef}
        picker={
          <SelectCategoryComboBox
            options={CLIKART_CATEGORIES}
            label="Category"
            value={category}
            onValueChange={setCategory}
            onOpenChange={reportAnyDropdownOpenChange('category')}
          />
        }
        filtersOpen={filtersExpanded}
        onFiltersOpenChange={setFiltersExpanded}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        filters={
          <ClikArtFilterControls
            keywordNames={keywordNames}
            selectedKeywords={selectedKeywords}
            onSelectedKeywordsChange={setSelectedKeywords}
            onKeywordOpenChange={reportAnyDropdownOpenChange('keyword')}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            placedFilter={placedFilter}
            onPlacedFilterChange={setPlacedFilter}
          />
        }
        showFilename={showFilename}
        onShowFilenameChange={setShowFilename}
        resultCount={results.length}
        showResultCount
        sort={{
          value: sortValue,
          options: CLIKART_SORT_OPTIONS,
          onValueChange: setSortValue,
          ariaLabel: 'Sort ClikArt',
          listboxLabel: 'Sort ClikArt by',
          onOpenChange: reportAnyDropdownOpenChange('sort'),
        }}
      >
        <ClikArtGallery
          isLoading={isLoading}
          isError={isError}
          resultCount={results.length}
          visibleResults={visibleResults}
          hasMore={hasMore}
          sentinelRef={sentinelRef}
          showFilename={showFilename}
          onPreviewOpenChange={reportPreviewOpenChange}
        />
      </TrayPanelLayout>
    </>
  )
}
